<template>
  <ReviewsProductInsightsReviews
    :view="view"
    :distribution="distribution"
    :aspects="aspects"
    :switches="switches"
    :product-title="product.title"
    :history="history"
    :insights="insights"
    :insights-loading="insightsLoading"
    :product-id="product.id"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  type ProductById,
  useInsightsProductsBySlugsQuery,
  useReviewsInsightsGrowthQuery,
  useReviewsInsightsLatestQuery,
  useReviewsInsightsSurveyResponseFacetsQuery,
  useReviewsTopRatedBadgeProgressQuery,
} from '@/gql/reviews'
import {
  mapReviewsAspects,
  mapReviewsDistribution,
  mapReviewsInsights,
  mapReviewsOwnInsights,
  mapReviewsScoreHistory,
  mapReviewsSwitches,
  reviewsSwitchSlugs,
  type ReviewsInsightsLoading,
} from '@/gql/reviews/mappers/insights'

import { comparisonWindow, growthWindow, isoToday, ratingHistoryStart, segmentSnapshots } from '../insightsWindow'
import { useRouterQueryInsightsTimeframe } from '../useRouterQueryInsightsTimeframe'
import ReviewsProductInsightsReviews from './ReviewsProductInsightsReviews.vue'

const QUERY_OPTIONS = { clientId: 'reviews', keepPreviousResult: true, fetchPolicy: 'cache-first' } as const

const props = defineProps<{ product: ProductById }>()

const { locale } = useI18n()
const { timeframe } = useRouterQueryInsightsTimeframe()

const growth = computed(() => growthWindow(timeframe.value, new Date()))

const latest = useReviewsInsightsLatestQuery(
  () => ({ productSlug: props.product.slug, mainCategorySlug: props.product.mainCategory.slug }),
  QUERY_OPTIONS,
)
const view = computed(() => mapReviewsOwnInsights(latest.result.value, !!latest.error.value, { locale: locale.value }))
const comparison = computed(() => comparisonWindow(timeframe.value, new Date()))
const growthQuery = useReviewsInsightsGrowthQuery(
  () => ({
    productSlug: props.product.slug,
    mainCategorySlug: props.product.mainCategory.slug,
    growthStart: growth.value.previousStart ?? growth.value.start,
    growthEnd: growth.value.end,
    windowStart: growth.value.start,
    historyStart: ratingHistoryStart({ ...growth.value, end: isoToday(new Date()) }),
    historyEnd: isoToday(new Date()),
    ...segmentSnapshots(growth.value),
    monthlyStart: comparison.value.previousStart ?? comparison.value.start,
    monthlyEnd: comparison.value.end,
  }),
  QUERY_OPTIONS,
)
const distribution = computed(() => mapReviewsDistribution(growthQuery.result.value, comparison.value, !!growthQuery.error.value))
const aspects = computed(() => mapReviewsAspects(growthQuery.result.value, comparison.value, !!growthQuery.error.value))
const history = computed(() => mapReviewsScoreHistory(growthQuery.result.value, !!growthQuery.error.value, { locale: locale.value }))
const switchSlugs = computed(() => reviewsSwitchSlugs(growthQuery.result.value, comparison.value, !!growthQuery.error.value))
const switchProducts = useInsightsProductsBySlugsQuery(
  () => ({ slugs: switchSlugs.value }),
  () => ({ clientId: 'reviews', fetchPolicy: 'cache-first', enabled: switchSlugs.value.length > 0 }),
)
const switches = computed(() =>
  mapReviewsSwitches(
    growthQuery.result.value,
    comparison.value,
    !!growthQuery.error.value,
    switchProducts.result.value?.listProducts ?? [],
  ),
)
const facets = useReviewsInsightsSurveyResponseFacetsQuery(() => ({ productSlug: props.product.slug }), QUERY_OPTIONS)
const badge = useReviewsTopRatedBadgeProgressQuery(() => ({ id: props.product.id }), QUERY_OPTIONS)

const insightsLoading = computed<ReviewsInsightsLoading>(() => ({
  distribution: growthQuery.loading.value,
  aspects: growthQuery.loading.value,
  quality: latest.loading.value || facets.loading.value,
  totalReviews: latest.loading.value,
  newReviews: growthQuery.loading.value,
  badge: badge.loading.value,
  byCategory: growthQuery.loading.value,
  bySegment: growthQuery.loading.value,
  sentiment: growthQuery.loading.value,
  history: growthQuery.loading.value,
  campaigns: latest.loading.value,
  switchedFrom: growthQuery.loading.value || switchProducts.loading.value,
}))

const insights = computed(() =>
  mapReviewsInsights(
    {
      latest: latest.result.value,
      growth: growthQuery.result.value,
      facets: facets.result.value,
      badge: badge.result.value,
      failed: {
        latest: !!latest.error.value,
        growth: !!growthQuery.error.value,
        facets: !!facets.error.value,
        badge: !!badge.error.value,
      },
    },
    { product: props.product, growthWindow: growth.value, locale: locale.value },
  ),
)
</script>
